// ABOUTME: Panel for adding insights to an existing report via drag-and-drop.
// ABOUTME: Toggle between pinned insights and all insights from investigations.
// ABOUTME: Remembers user's toggle preference in localStorage.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  X,
  Pin,
  BarChart3,
  Table2,
  FileText,
  LayoutGrid,
  MessageSquare,
  GripVertical,
  AlertTriangle,
  Eye,
  Loader2,
  Layers,
  Search,
  Check,
} from 'lucide-react';
import { useApp } from '@/context';
import { apiClient } from '@/services/api';
import { InsightPreviewModal } from '@/components/layout/InsightPreviewModal';
import type { PinnedItem, PinnedItemType } from '@/types';
// types used for InvestigationInsight construction

export const INSIGHT_DRAG_TYPE = 'application/x-oleum-insight';
/** Drag type for unpinned insights — value is JSON: { messageId, insightId } */
export const INSIGHT_REF_DRAG_TYPE = 'application/x-oleum-insight-ref';

const INSIGHT_VIEW_KEY = 'oleum-add-insight-view';

type InsightView = 'pinned' | 'all';

/** An insight extracted from investigation messages, shaped like a PinnedItem for reuse */
interface InvestigationInsight {
  id: string; // `${messageId}:${insightId}`
  messageId: string;
  insightId: string;
  title: string;
  type: PinnedItemType;
  insightOverview: string | null;
  investigationId: string;
  investigationTitle: string;
  data: Record<string, unknown>;
}

interface AddInsightPanelProps {
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
  onAdd: (pinIds: string[]) => Promise<void>;
  /** Add an unpinned insight by auto-pinning then adding. Returns when done. */
  onAddByRef?: (messageId: string, insightId: string) => Promise<void>;
  /** Set of "messageId:insightId" keys for insights already in the report */
  insightKeysInReport?: Set<string>;
}

function getTypeIcon(type: PinnedItemType) {
  switch (type) {
    case 'chart':
      return <BarChart3 className="w-3.5 h-3.5" />;
    case 'table':
      return <Table2 className="w-3.5 h-3.5" />;
    case 'chart-table':
      return <LayoutGrid className="w-3.5 h-3.5" />;
    default:
      return <FileText className="w-3.5 h-3.5" />;
  }
}

function getTypeBadgeColor(type: PinnedItemType): string {
  switch (type) {
    case 'chart':
      return 'bg-blue-50 text-blue-600';
    case 'table':
      return 'bg-emerald-50 text-emerald-600';
    case 'summary':
    case 'text':
      return 'bg-purple-50 text-purple-600';
    case 'chart-table':
      return 'bg-amber-50 text-amber-600';
    default:
      return 'bg-surface-100 text-surface-600';
  }
}

// NOTE: This component assumes the parent fully unmounts it on close
// (not just hides it), so local state (draggingId, view, etc.) resets
// automatically. If this changes, add a useEffect to clear selection
// state when the panel closes.
export function AddInsightPanel({
  isLoading,
  error,
  onClose,
  onAdd,
  onAddByRef,
  insightKeysInReport,
}: AddInsightPanelProps) {
  const { pinnedItems } = useApp();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<PinnedItem | null>(null);
  const [view, setView] = useState<InsightView>(() => {
    const stored = localStorage.getItem(INSIGHT_VIEW_KEY);
    return stored === 'all' ? 'all' : 'pinned';
  });
  const [allInsights, setAllInsights] = useState<InvestigationInsight[]>([]);
  const [allInsightsLoading, setAllInsightsLoading] = useState(false);
  const [allInsightsLoaded, setAllInsightsLoaded] = useState(false);

  // Persist view preference
  const handleViewChange = useCallback((newView: InsightView) => {
    setView(newView);
    localStorage.setItem(INSIGHT_VIEW_KEY, newView);
  }, []);

  // Load all insights when switching to "all" view
  useEffect(() => {
    if (view !== 'all' || allInsightsLoaded) return;
    let cancelled = false;

    const loadAllInsights = async () => {
      setAllInsightsLoading(true);
      try {
        const { investigations } = await apiClient.investigations.list({ limit: 50 });
        const insights: InvestigationInsight[] = [];

        // Fetch messages for each investigation in parallel (max 5 concurrent)
        const batchSize = 5;
        for (let i = 0; i < investigations.length; i += batchSize) {
          if (cancelled) return;
          const batch = investigations.slice(i, i + batchSize);
          const results = await Promise.allSettled(
            batch.map((inv) => apiClient.investigations.get(inv.id))
          );
          for (let j = 0; j < results.length; j++) {
            const result = results[j];
            if (result.status !== 'fulfilled') continue;
            const inv = batch[j];
            const { messages } = result.value;
            for (const msg of messages) {
              if (!msg.insights?.length) continue;
              for (const insight of msg.insights) {
                insights.push({
                  id: `${msg.id}:${insight.id}`,
                  messageId: msg.id,
                  insightId: insight.id,
                  title: insight.title,
                  type: insight.type as PinnedItemType,
                  insightOverview: insight.insight_overview || null,
                  investigationId: inv.id,
                  investigationTitle: inv.title,
                  data: insight as unknown as Record<string, unknown>,
                });
              }
            }
          }
        }

        if (!cancelled) {
          setAllInsights(insights);
          setAllInsightsLoaded(true);
        }
      } catch (err) {
        console.error('Failed to load all insights:', err);
      } finally {
        if (!cancelled) setAllInsightsLoading(false);
      }
    };

    loadAllInsights();
    return () => { cancelled = true; };
  }, [view, allInsightsLoaded]);

  // Group pins by investigation
  const groupedPins = useMemo(() => {
    const groups: Map<string, { title: string; items: typeof pinnedItems }> = new Map();
    for (const item of pinnedItems) {
      const key = item.sourceInvestigationId;
      if (!groups.has(key)) {
        groups.set(key, { title: item.sourceInvestigationTitle, items: [] });
      }
      groups.get(key)!.items.push(item);
    }
    return Array.from(groups.entries());
  }, [pinnedItems]);

  // Group all insights by investigation
  const groupedAllInsights = useMemo(() => {
    const groups: Map<string, { title: string; items: InvestigationInsight[] }> = new Map();
    for (const item of allInsights) {
      const key = item.investigationId;
      if (!groups.has(key)) {
        groups.set(key, { title: item.investigationTitle, items: [] });
      }
      groups.get(key)!.items.push(item);
    }
    return Array.from(groups.entries());
  }, [allInsights]);

  // Pinned insight IDs for marking which all-insights are already pinned
  const pinnedInsightKeys = useMemo(() => {
    return new Set(pinnedItems.map((p) => `${p.messageId}:${p.insightId}`));
  }, [pinnedItems]);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedQuery(value), 200);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // Filter grouped pins by search
  const filteredGroupedPins = useMemo(() => {
    if (!debouncedQuery.trim()) return groupedPins;
    const q = debouncedQuery.toLowerCase();
    return groupedPins
      .map(([id, group]) => {
        const filtered = group.items.filter(
          (item) =>
            item.title.toLowerCase().includes(q) ||
            item.insightOverview?.toLowerCase().includes(q) ||
            group.title.toLowerCase().includes(q)
        );
        return [id, { ...group, items: filtered }] as typeof groupedPins[number];
      })
      .filter(([, group]) => group.items.length > 0);
  }, [groupedPins, debouncedQuery]);

  // Filter grouped all insights by search
  const filteredGroupedAllInsights = useMemo(() => {
    if (!debouncedQuery.trim()) return groupedAllInsights;
    const q = debouncedQuery.toLowerCase();
    return groupedAllInsights
      .map(([id, group]) => {
        const filtered = group.items.filter(
          (item) =>
            item.title.toLowerCase().includes(q) ||
            item.insightOverview?.toLowerCase().includes(q) ||
            group.title.toLowerCase().includes(q)
        );
        return [id, { ...group, items: filtered }] as typeof groupedAllInsights[number];
      })
      .filter(([, group]) => group.items.length > 0);
  }, [groupedAllInsights, debouncedQuery]);

  const handleDragStart = useCallback((e: React.DragEvent, pinId: string, title: string) => {
    e.dataTransfer.setData(INSIGHT_DRAG_TYPE, pinId);
    e.dataTransfer.setData('text/plain', title);
    e.dataTransfer.effectAllowed = 'copy';
    setDraggingId(pinId);
  }, []);

  // Drag start for unpinned insights — stores ref as JSON
  const handleDragStartRef = useCallback((e: React.DragEvent, messageId: string, insightId: string, title: string) => {
    e.dataTransfer.setData(INSIGHT_REF_DRAG_TYPE, JSON.stringify({ messageId, insightId }));
    e.dataTransfer.setData('text/plain', title);
    e.dataTransfer.effectAllowed = 'copy';
    setDraggingId(`${messageId}:${insightId}`);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
  }, []);

  // Double-click as a quick-add alternative to drag (pinned items)
  const handleDoubleClick = useCallback(async (pinId: string) => {
    if (isLoading) return;
    try {
      await onAdd([pinId]);
    } catch {
      // Error handled by parent via error prop
    }
  }, [isLoading, onAdd]);

  // Double-click for unpinned insights — auto-pin then add
  const handleDoubleClickRef = useCallback(async (messageId: string, insightId: string) => {
    if (isLoading || !onAddByRef) return;
    try {
      await onAddByRef(messageId, insightId);
    } catch {
      // Error handled by parent
    }
  }, [isLoading, onAddByRef]);

  return (
    <div className="w-[380px] flex-shrink-0 bg-surface-0 border-l border-surface-200 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Pin className="w-4 h-4 text-primary-600" />
          <span className="text-sm font-semibold text-surface-800">Add Insights</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 hover:bg-surface-100 rounded-sm transition-colors cursor-pointer"
        >
          <X className="w-4 h-4 text-surface-500" />
        </button>
      </div>

      {/* View toggle */}
      <div className="px-4 py-2 border-b border-surface-200 flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={() => handleViewChange('pinned')}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-sm transition-colors cursor-pointer ${
            view === 'pinned'
              ? 'bg-primary-50 text-primary-700 font-medium'
              : 'text-surface-600 hover:bg-surface-50'
          }`}
        >
          <Pin className="w-3 h-3" />
          Pinned
        </button>
        <button
          type="button"
          onClick={() => handleViewChange('all')}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-sm transition-colors cursor-pointer ${
            view === 'all'
              ? 'bg-primary-50 text-primary-700 font-medium'
              : 'text-surface-600 hover:bg-surface-50'
          }`}
        >
          <Layers className="w-3 h-3" />
          All Insights
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-2 border-b border-surface-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search insights..."
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-surface-50 border border-surface-200 rounded-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-600/25 focus:border-primary-600"
          />
        </div>
      </div>

      {/* Drag hint */}
      {((view === 'pinned' && pinnedItems.length > 0) || (view === 'all' && allInsights.length > 0)) && (
        <div className="px-4 py-2.5 bg-primary-50/50 border-b border-primary-100 flex-shrink-0">
          <p className="text-[11px] text-primary-700 font-medium">
            {view === 'pinned'
              ? 'Drag an insight into the report to add it. Double-click to quick-add.'
              : 'Double-click to add an insight to the end of the report, or drag and drop it wherever you\'d like.'}
          </p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-error-light border-b border-error/30 flex items-center gap-2 flex-shrink-0">
          <AlertTriangle className="w-3.5 h-3.5 text-error flex-shrink-0" />
          <p className="text-xs text-error">{error}</p>
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="px-4 py-2 bg-primary-50 border-b border-primary-100 flex items-center gap-2 flex-shrink-0">
          <div className="w-3.5 h-3.5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-primary-700 font-medium">Adding insight to report...</p>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3">
        {view === 'pinned' ? (
          /* Pinned insights view */
          pinnedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Pin className="w-8 h-8 text-surface-300 mb-3" />
              <p className="text-sm text-surface-600 mb-1">No pinned insights</p>
              <p className="text-xs text-surface-500">
                Pin insights from your investigations, or switch to "All Insights."
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGroupedPins.map(([investigationId, group]) => (
                <div key={investigationId}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <MessageSquare className="w-3 h-3 text-surface-400" />
                    <span className="text-[11px] font-medium text-surface-500 uppercase tracking-wide truncate">
                      {group.title}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {group.items.map((item) => {
                      const isDragging = draggingId === item.id;
                      const isInReport = insightKeysInReport?.has(`${item.messageId}:${item.insightId}`);
                      return (
                        <div
                          key={item.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, item.id, item.title)}
                          onDragEnd={handleDragEnd}
                          onDoubleClick={() => handleDoubleClick(item.id)}
                          className={`group flex items-start gap-2 rounded-md border p-3 transition-all cursor-grab active:cursor-grabbing select-none ${
                            isDragging
                              ? 'opacity-30 scale-95 border-primary-400 bg-primary-50'
                              : isInReport
                                ? 'border-emerald-200 bg-emerald-50/30 hover:border-primary-300 hover:shadow-sm'
                                : 'border-surface-200 bg-surface-0 hover:border-primary-300 hover:shadow-sm hover:bg-surface-25'
                          }`}
                        >
                          <div className="flex-shrink-0 mt-0.5 text-surface-300 group-hover:text-surface-500 transition-colors">
                            <GripVertical className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${getTypeBadgeColor(item.type)}`}>
                                {getTypeIcon(item.type)}
                                {item.type}
                              </span>
                              {isInReport && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600">
                                  <Check className="w-2.5 h-2.5" />
                                  in report
                                </span>
                              )}
                            </div>
                            <p className="text-[13px] text-surface-800 font-medium leading-snug">
                              {item.title}
                            </p>
                            {item.insightOverview && (
                              <p className="text-[11px] text-surface-500 mt-1 line-clamp-2 leading-relaxed">
                                {item.insightOverview}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setPreviewItem(item);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="flex-shrink-0 p-1.5 rounded-sm text-surface-400 hover:text-primary-600 hover:bg-surface-100 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                            title="Preview insight"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* All insights view */
          allInsightsLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-3" />
              <p className="text-sm text-surface-600">Loading insights from investigations...</p>
            </div>
          ) : allInsights.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Layers className="w-8 h-8 text-surface-300 mb-3" />
              <p className="text-sm text-surface-600 mb-1">No insights found</p>
              <p className="text-xs text-surface-500">
                Run investigations to generate insights you can add to reports.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGroupedAllInsights.map(([investigationId, group]) => (
                <div key={investigationId}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <MessageSquare className="w-3 h-3 text-surface-400" />
                    <span className="text-[11px] font-medium text-surface-500 uppercase tracking-wide truncate">
                      {group.title}
                    </span>
                    <span className="text-[10px] text-surface-400">
                      {group.items.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {group.items.map((item) => {
                      const isPinned = pinnedInsightKeys.has(`${item.messageId}:${item.insightId}`);
                      const isInReport = insightKeysInReport?.has(`${item.messageId}:${item.insightId}`);
                      const isDragging = draggingId === item.id;
                      return (
                        <div
                          key={item.id}
                          draggable
                          onDragStart={(e) => {
                            if (isPinned) {
                              // Find matching pin ID and use standard drag
                              const pin = pinnedItems.find(
                                (p) => p.messageId === item.messageId && p.insightId === item.insightId
                              );
                              if (pin) {
                                handleDragStart(e, pin.id, item.title);
                                return;
                              }
                            }
                            handleDragStartRef(e, item.messageId, item.insightId, item.title);
                          }}
                          onDragEnd={handleDragEnd}
                          onDoubleClick={() => handleDoubleClickRef(item.messageId, item.insightId)}
                          className={`group flex items-start gap-2 rounded-md border p-3 transition-all cursor-grab active:cursor-grabbing select-none ${
                            isDragging
                              ? 'opacity-30 scale-95 border-primary-400 bg-primary-50'
                              : isInReport
                                ? 'border-emerald-200 bg-emerald-50/30 hover:border-primary-300 hover:shadow-sm'
                                : 'border-surface-200 bg-surface-0 hover:border-primary-300 hover:shadow-sm hover:bg-surface-25'
                          }`}
                        >
                          {/* Drag handle */}
                          <div className="flex-shrink-0 mt-0.5 text-surface-300 group-hover:text-surface-500 transition-colors">
                            <GripVertical className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${getTypeBadgeColor(item.type)}`}>
                                {getTypeIcon(item.type)}
                                {item.type}
                              </span>
                              {isPinned && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-50 text-primary-600">
                                  <Pin className="w-2.5 h-2.5" />
                                  pinned
                                </span>
                              )}
                              {isInReport && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600">
                                  <Check className="w-2.5 h-2.5" />
                                  in report
                                </span>
                              )}
                            </div>
                            <p className="text-[13px] text-surface-800 font-medium leading-snug">
                              {item.title}
                            </p>
                            {item.insightOverview && (
                              <p className="text-[11px] text-surface-500 mt-1 line-clamp-2 leading-relaxed">
                                {item.insightOverview}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setPreviewItem({
                                id: item.id,
                                title: item.title,
                                type: item.type,
                                timestamp: new Date(),
                                data: item.data,
                                insightOverview: item.insightOverview ?? undefined,
                                messageId: item.messageId,
                                insightId: item.insightId,
                                sourceInvestigationId: item.investigationId,
                                sourceInvestigationTitle: item.investigationTitle,
                              });
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="flex-shrink-0 p-1.5 rounded-sm text-surface-400 hover:text-primary-600 hover:bg-surface-100 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                            title="Preview insight"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Insight Preview Modal */}
      <InsightPreviewModal
        item={previewItem}
        onClose={() => setPreviewItem(null)}
      />
    </div>
  );
}
